import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { isMunicipalityContact, isStaff, isTeacher, useAuth } from '../auth.tsx';
import { cx } from './ui.tsx';

export function Logo({ light = true }: { light?: boolean }) {
  return (
    <span className="flex items-center gap-2.5">
      <img src="/favicon.svg" alt="" className="h-8 w-8" />
      <span className={cx('text-lg font-bold tracking-wide', light ? 'text-white' : 'text-night-900')}>
        Aurora
      </span>
    </span>
  );
}

function NavItem({ to, children, onClick }: { to: string; children: ReactNode; onClick?: () => void }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cx(
          'rounded-lg px-3 py-2 text-sm font-semibold transition',
          isActive ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white',
        )
      }
    >
      {children}
    </NavLink>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const links = user ? (
    <>
      <NavItem to="/kurs" onClick={close}>
        Kursen
      </NavItem>
      <NavItem to="/dagbok" onClick={close}>
        Dagbok
      </NavItem>
      <NavItem to="/intyg" onClick={close}>
        Intyg
      </NavItem>
      <NavItem to="/konto" onClick={close}>
        Mitt konto
      </NavItem>
      {isStaff(user) && (
        <NavItem to="/larare" onClick={close}>
          {isTeacher(user) ? 'Lärare' : 'Rättning'}
        </NavItem>
      )}
      {isMunicipalityContact(user) && (
        <NavItem to="/kommun" onClick={close}>
          Kommun
        </NavItem>
      )}
      <button
        type="button"
        onClick={async () => {
          close();
          await logout();
          navigate('/');
        }}
        className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/80 hover:bg-white/10 hover:text-white"
      >
        Logga ut
      </button>
    </>
  ) : (
    <>
      <NavItem to="/om" onClick={close}>
        Om kursen
      </NavItem>
      <NavItem to="/kontakt" onClick={close}>
        Kontakt
      </NavItem>
      <NavItem to="/logga-in" onClick={close}>
        Logga in
      </NavItem>
      <NavItem to="/registrera" onClick={close}>
        Skapa konto
      </NavItem>
    </>
  );

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2"
      >
        Hoppa till innehållet
      </a>
      <header className="aurora-band text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to={user ? '/kurs' : '/'} aria-label="Aurora, till startsidan">
            <Logo />
          </Link>
          <nav aria-label="Huvudmeny" className="hidden items-center gap-1 md:flex">
            {links}
          </nav>
          <button
            type="button"
            className="rounded-lg p-2 text-white hover:bg-white/10 md:hidden"
            aria-expanded={open}
            aria-controls="mobilmeny"
            onClick={() => setOpen(!open)}
          >
            <span className="sr-only">Meny</span>
            <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
              <path
                d={open ? 'M6 6l12 12M18 6 6 18' : 'M4 7h16M4 12h16M4 17h16'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {open && (
          <nav
            id="mobilmeny"
            aria-label="Huvudmeny"
            className="flex flex-col gap-1 border-t border-white/10 px-4 py-3 md:hidden"
          >
            {links}
          </nav>
        )}
      </header>
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-night-800/10 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-sm text-muted sm:flex-row sm:justify-between">
          <p>Aurora – AI-litteracitet för lärare i Norrbotten. Luleå tekniska universitet.</p>
          <p className="flex flex-wrap gap-4">
            <Link to="/om" className="underline">
              Om Aurora
            </Link>
            <Link to="/kontakt" className="underline">
              Kontakt
            </Link>
            <Link to="/norrbotten" className="underline">
              Norrbottenskartan
            </Link>
            <Link to="/integritet" className="underline">
              Personuppgifter och kakor
            </Link>
            <Link to="/tillganglighet" className="underline">
              Tillgänglighet
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

export function Container({ children, narrow }: { children: ReactNode; narrow?: boolean }) {
  return (
    <div className={cx('mx-auto px-4 py-8 sm:py-10', narrow ? 'max-w-lg' : 'max-w-6xl')}>{children}</div>
  );
}
